'use strict';
(() => {
  if (window.IdolnestArtistMotion) return;
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const idle = callback => 'requestIdleCallback' in window ? requestIdleCallback(callback,{timeout:900}) : setTimeout(callback,300);
  const cancelIdle = id => 'cancelIdleCallback' in window ? cancelIdleCallback(id) : clearTimeout(id);
  const ease = 'cubic-bezier(.2,.75,.25,1)';

  function mount(root) {
    if (instances.has(root)) return;
    const stage = root.querySelector('[data-in-artist-stage]');
    const tabs = root.querySelector('.in-artist-categories');
    const inputs = [...root.querySelectorAll('[data-in-artist-category-input]')];
    const panels = [...root.querySelectorAll('[data-in-artist-panel]')];
    if (!stage || !tabs || !inputs.length || !panels.length) return;
    const abort = new AbortController();
    const indicator = document.createElement('span');
    indicator.className = 'in-artist-tab-indicator';
    indicator.setAttribute('aria-hidden','true');
    const state = {active:inputs.find(input=>input.checked)?.value || inputs[0].value,epoch:0,animations:[],metrics:new Map(),warming:new Map(),idle:0,baseWidth:1};
    const panelFor = value => panels.find(panel=>panel.dataset.inArtistPanel===value);

    function settle() {
      for (const panel of panels) {
        const active = panel.dataset.inArtistPanel === state.active;
        panel.classList.toggle('is-current',active);
        panel.classList.remove('is-leaving');
        panel.inert = !active;
        panel.setAttribute('aria-hidden',String(!active));
      }
    }

    function stop() {
      ++state.epoch;
      for (const animation of state.animations) animation.cancel();
      state.animations = [];
      settle();
    }

    function measure() {
      const parent = tabs.getBoundingClientRect();
      const measures = inputs.map(input=>{
        const rect = input.nextElementSibling.getBoundingClientRect();
        return [input.value,{x:rect.left-parent.left+tabs.scrollLeft,width:rect.width}];
      });
      state.metrics = new Map(measures);
      state.baseWidth = Math.max(1,...measures.map(([,metric])=>metric.width));
      const metric = state.metrics.get(state.active);
      if (metric) {
        indicator.style.width = state.baseWidth+'px';
        indicator.style.transform = `translate3d(${metric.x}px,0,0) scaleX(${metric.width/state.baseWidth})`;
      }
    }

    function warm(value) {
      const panel = panelFor(value);
      if (!panel) return Promise.resolve();
      const images = [...panel.querySelectorAll('img')].slice(0,5);
      for (const img of images) img.fetchPriority = value === state.active ? 'auto' : 'low';
      if (state.warming.has(value)) return state.warming.get(value);
      const work = Promise.allSettled(images.map(img=>{
        img.decoding = 'async';
        img.loading = 'eager';
        return typeof img.decode === 'function' ? img.decode() : Promise.resolve();
      })).then(()=>{if(root.isConnected)panel.dataset.imagesPrepared=String(images.every(img=>img.complete&&img.naturalWidth>0));});
      state.warming.set(value,work);
      return work;
    }

    function change(value) {
      const incoming = panelFor(value);
      if (!incoming || value === state.active) return;
      const outgoing = panelFor(state.active);
      const prior = state.metrics.get(state.active);
      const metric = state.metrics.get(value);
      const direction = inputs.findIndex(input=>input.value===value) > inputs.findIndex(input=>input.value===state.active) ? 1 : -1;
      const snapshot = panel => {
        if (!panel?.classList.contains('is-current') && !panel?.classList.contains('is-leaving')) return null;
        const style = getComputedStyle(panel);
        return {opacity:style.opacity,transform:style.transform};
      };
      const fromOld = state.animations.length ? snapshot(outgoing) : null;
      const fromNew = state.animations.length ? snapshot(incoming) : null;
      const fromIndicator = state.animations.length ? getComputedStyle(indicator).transform : prior ? `translate3d(${prior.x}px,0,0) scaleX(${prior.width/state.baseWidth})` : null;
      const epoch = ++state.epoch;
      for (const animation of state.animations) animation.cancel();
      state.animations = [];
      state.active = value;
      settle();
      incoming.scrollLeft = 0;
      warm(value);
      if (metric) {
        indicator.style.transform = `translate3d(${metric.x}px,0,0) scaleX(${metric.width/state.baseWidth})`;
      }
      if (reduced.matches || !incoming.animate) return;
      outgoing?.classList.add('is-leaving');
      const animations = [];
      if (outgoing) animations.push(outgoing.animate([
        fromOld || {opacity:1,transform:'translate3d(0,0,0)'},
        {opacity:0,transform:`translate3d(${-direction*14}px,0,0)`}
      ],{duration:170,easing:'ease-out'}));
      animations.push(incoming.animate([
        fromNew || {opacity:.28,transform:`translate3d(${direction*22}px,0,0)`},
        {opacity:1,transform:'translate3d(0,0,0)'}
      ],{duration:320,easing:ease}));
      if (prior && metric) animations.push(indicator.animate([
        {transform:fromIndicator},
        {transform:`translate3d(${metric.x}px,0,0) scaleX(${metric.width/state.baseWidth})`}
      ],{duration:280,easing:ease}));
      state.animations = animations;
      Promise.allSettled(animations.map(animation=>animation.finished)).then(()=>{
        if (epoch !== state.epoch) return;
        state.animations = [];
        settle();
      });
    }

    const intent = event => {
      const target = event.target.closest?.('.in-artist-category-option');
      const input = target?.querySelector('[data-in-artist-category-input]');
      if (input) warm(input.value);
    };
    tabs.append(indicator);
    root.classList.add('in-artist-motion-ready');
    settle();
    measure();
    root.addEventListener('change',event=>{
      if (event.target.matches('[data-in-artist-category-input]')) change(event.target.value);
    },{signal:abort.signal});
    tabs.addEventListener('pointerover',intent,{passive:true,signal:abort.signal});
    tabs.addEventListener('focusin',intent,{signal:abort.signal});
    root.addEventListener('pointerdown',event=>{
      if (event.target.closest('.artist-card')) stop();
    },{capture:true,signal:abort.signal});
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    resize?.observe(tabs);
    const nearby = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries=>{
      if (!entries.some(entry=>entry.isIntersecting)) return;
      nearby.disconnect();
      state.idle = idle(()=>{
        if (!root.isConnected) return;
        for (const input of inputs) if (!navigator.connection?.saveData || input.checked) warm(input.value);
      });
    },{rootMargin:'500px'}) : null;
    if (nearby) nearby.observe(root);
    else state.idle = idle(()=>warm(state.active));
    instances.set(root,{change,stop,destroy(){stop();abort.abort();resize?.disconnect();nearby?.disconnect();cancelIdle(state.idle);indicator.remove();root.classList.remove('in-artist-motion-ready');panels.forEach(panel=>{panel.inert=false;panel.removeAttribute('aria-hidden');});}});
  }

  function init(scope=document) {
    for (const [root,instance] of instances) if (!root.isConnected) {instance.destroy();instances.delete(root);}
    const roots = [...(scope.matches?.('[data-in-shared-artists]')?[scope]:[]),...scope.querySelectorAll('[data-in-shared-artists]')];
    roots.forEach(mount);
  }
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    for (const [root,instance] of instances) if (event.target.contains(root)) {instance.destroy();instances.delete(root);}
  });
  document.addEventListener('shopify:block:select',event=>{
    const card=event.target.closest?.('[data-in-shared-artist]');
    const root=card?.closest('[data-in-shared-artists]');
    const instance=instances.get(root);
    if(instance&&card){instance.change(card.dataset.inArtistCategory);instance.stop();}
  });
  reduced.addEventListener('change',()=>{if(reduced.matches)for(const instance of instances.values())instance.stop();});
  window.addEventListener('pageshow',event=>{if(event.persisted)for(const instance of instances.values())instance.stop();});
  window.IdolnestArtistMotion={init};
  init();
})();
